import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { dashboardFinancieroService } from '../../services/tiendaService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';

const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtPct = (n) => typeof n === 'number' ? `${fmt(n, 1)}%` : '—';

const TiendaDashboardFinanciero = () => {
  const [resumen, setResumen] = useState(null);
  const [evolucion, setEvolucion] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, evo] = await Promise.all([
        dashboardFinancieroService?.getResumen(),
        dashboardFinancieroService?.getEvolucionVentas(6),
      ]);
      setResumen(res);
      setEvolucion(evo);
    } catch (err) {
      setError(err?.message || 'Error al cargar dashboard financiero');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const kpiCards = [
    { label: 'Ventas del Mes', value: fmtEur(resumen?.ventas?.ventasMes), icon: 'TrendingUp', color: ACCENT, sub: `Hoy: ${fmtEur(resumen?.ventas?.ventasHoy)}` },
    { label: 'Compras del Mes', value: fmtEur(resumen?.compras?.comprasMes), icon: 'ShoppingBag', color: BLUE, sub: `IVA: ${fmtEur(resumen?.compras?.ivaSoportado)}` },
    { label: 'Margen Bruto', value: fmtPct(resumen?.margenBruto), icon: 'Percent', color: GOLD, sub: 'Ventas - Compras' },
    { label: 'Ingresos Caja Mes', value: fmtEur(resumen?.caja?.totalIngresos), icon: 'Wallet', color: PURPLE, sub: `Gastos: ${fmtEur(resumen?.caja?.totalGastos)}` },
    { label: 'Beneficio Neto Caja', value: fmtEur(resumen?.caja?.beneficioNeto), icon: 'DollarSign', color: '#4ADE80', sub: `${resumen?.caja?.diasRegistrados ?? 0} días` },
    { label: 'Valor Inventario', value: fmtEur(resumen?.inventario?.valorCoste), icon: 'Package', color: '#FB923C', sub: `${resumen?.inventario?.referencias ?? 0} refs` },
  ];

  const pagoData = resumen?.caja ? [
    { name: 'Efectivo', value: resumen?.caja?.totalEfectivo || 0, color: ACCENT },
    { name: 'Tarjeta', value: resumen?.caja?.totalTarjeta || 0, color: GOLD },
    { name: 'Bizum', value: resumen?.caja?.totalBizum || 0, color: PURPLE },
  ]?.filter(d => d?.value > 0) : [];

  const canalData = resumen?.ventas ? [
    { name: 'Físico', value: resumen?.ventas?.ventasFisico || 0, color: ACCENT },
    { name: 'Online', value: resumen?.ventas?.ventasOnline || 0, color: BLUE },
  ]?.filter(d => d?.value > 0) : [];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="BarChart2" size={18} style={{ color: GOLD }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Dashboard Financiero</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Resumen financiero · Tienda General</p>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
          <Icon name="RefreshCw" size={13} />Actualizar
        </button>
      </div>
      {error && (
        <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
        </div>
      )}
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-xl p-4 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
              <Icon name={kpi?.icon} size={15} style={{ color: kpi?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white text-xl">{loading ? '...' : kpi?.value}</p>
            <div>
              <p className="font-oranienbaum text-white/50 text-xs leading-tight">{kpi?.label}</p>
              <p className="font-oranienbaum text-white/25 text-[10px]">{loading ? '' : kpi?.sub}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Evolución ventas */}
        <div className="lg:col-span-2 rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-oranienbaum font-semibold text-white text-sm">Evolución de Ventas (6 meses)</h3>
            <div className="flex items-center gap-3 text-[10px] font-oranienbaum text-white/40">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: ACCENT }} />Físico</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: BLUE }} />Online</span>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" />
            </div>
          ) : evolucion?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Icon name="BarChart2" size={24} className="text-white/15" />
              <p className="font-oranienbaum text-white/25 text-xs">Sin datos de ventas aún</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={evolucion} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="mes" tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={(v) => [`${fmt(v)} €`]} />
                <Bar dataKey="fisico" fill={ACCENT} radius={[3, 3, 0, 0]} name="Físico" />
                <Bar dataKey="online" fill={BLUE} radius={[3, 3, 0, 0]} name="Online" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Distribución por canal */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-4">Ventas por Canal</h3>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
            </div>
          ) : canalData?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Icon name="PieChart" size={24} className="text-white/15" />
              <p className="font-oranienbaum text-white/25 text-xs">Sin datos</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={canalData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" paddingAngle={3}>
                    {canalData?.map((d, idx) => <Cell key={idx} fill={d?.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={(v) => [`${fmt(v)} €`]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {canalData?.map((d, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: d?.color }} />
                      <span className="font-oranienbaum text-white/60 text-xs">{d?.name}</span>
                    </div>
                    <span className="font-oranienbaum text-white/80 text-xs">{fmtEur(d?.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Forma de pago */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-4">Formas de Pago (Caja Mes)</h3>
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
            </div>
          ) : pagoData?.length === 0 ? (
            <p className="font-oranienbaum text-white/25 text-xs text-center py-8">Sin datos de caja</p>
          ) : (
            <div className="space-y-3">
              {pagoData?.map((d, i) => {
                const total = pagoData?.reduce((s, x) => s + x?.value, 0);
                const pct = total > 0 ? (d?.value / total) * 100 : 0;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-oranienbaum text-white/60 text-xs">{d?.name}</span>
                      <span className="font-oranienbaum text-white/80 text-xs">{fmtEur(d?.value)} · {fmt(pct, 1)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: d?.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Alertas */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-4">Alertas de Inventario</h3>
          <div className="space-y-3">
            {[
              { label: 'Bajo Mínimo', value: resumen?.inventario?.bajoMinimo ?? 0, color: '#FBBF24', icon: 'AlertTriangle' },
              { label: 'Agotados', value: resumen?.inventario?.agotados ?? 0, color: '#F87171', icon: 'XCircle' },
              { label: 'Facturas Pendientes', value: resumen?.compras?.facturasPendientes ?? 0, color: '#60A5FA', icon: 'Clock' },
            ]?.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl" style={{ background: `${a?.color}08`, border: `1px solid ${a?.color}18` }}>
                <div className="flex items-center gap-2">
                  <Icon name={a?.icon} size={14} style={{ color: a?.color }} />
                  <span className="font-oranienbaum text-white/70 text-sm">{a?.label}</span>
                </div>
                <span className="font-oranienbaum font-bold text-sm" style={{ color: a?.color }}>{loading ? '...' : a?.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TiendaDashboardFinanciero;
